/**
 * Transaction process graph for plain inquiries.
 * Defines the state machine for handling inquiry-based transactions without payment.
 */

/**
 * Enum-like object for transaction transitions.
 * Must sync with Marketplace API transition values.
 */
export const transitions = {
  INQUIRE_WITHOUT_PAYMENT: 'transition/inquire-without-payment',
};

/**
 * Enum-like object for transaction states.
 * Used internally to describe the transaction process flow.
 */
export const states = {
  INITIAL: 'initial',
  FREE_INQUIRY: 'free-inquiry',
};

/**
 * Transaction process graph configuration.
 * Follows Xstate-inspired format for state machine definition.
 * @see https://xstate.js.org/docs/
 * @type {Object}
 * @property {string} id - Unique identifier for the state machine
 * @property {string} initial - Initial state of the transaction
 * @property {Object} states - State definitions with transitions
 */
export const graph = {
  id: 'default-inquiry/release-1',
  initial: states.INITIAL,
  states: {
    [states.INITIAL]: {
      on: {
        [transitions.INQUIRE_WITHOUT_PAYMENT]: states.FREE_INQUIRY,
      },
    },
    [states.FREE_INQUIRY]: { type: 'final' },
  },
};

/**
 * Checks if a transition should be displayed in the transaction history (e.g., ActivityFeed).
 * @param {string} transition - The transition to check
 * @returns {boolean} True if the transition is relevant for history display
 */
export const isRelevantPastTransition = transition => {
  return [transitions.INQUIRE_WITHOUT_PAYMENT].includes(transition);
};

/**
 * Checks if the transition represents a customer review.
 * @param {string} transition - The transition to check
 * @returns {boolean} False (no reviews in this process)
 */
export const isCustomerReview = transition => {
  return false;
};

/**
 * Checks if the transition represents a provider review.
 * @param {string} transition - The transition to check
 * @returns {boolean} False (no reviews in this process)
 */
export const isProviderReview = transition => {
  return false;
};

/**
 * Checks if the transition is privileged (requires backend handling).
 * @param {string} transition - The transition to check
 * @returns {boolean} False (no privileged transitions in this process)
 */
export const isPrivileged = transition => {
  return false;
};

/**
 * Checks if the transaction is completed.
 * @param {string} transition - The transition to check
 * @returns {boolean} False (no completion state in this process)
 */
export const isCompleted = transition => {
  return false;
};

/**
 * Checks if the transaction is refunded.
 * @param {string} transition - The transition to check
 * @returns {boolean} False (no refunds in this process)
 */
export const isRefunded = transition => {
  return false;
};

/**
 * States that require provider attention.
 * @type {Array<string>}
 */
export const statesNeedingProviderAttention = [];
